import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import api from "@/lib/api";
import { loadCashfreeSdk } from "@/lib/cashfree";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Loader2, Sparkles, FileText } from "lucide-react";

const SAMPLE_REPORT_URL = `${process.env.REACT_APP_BACKEND_URL}/assets/sample_report.pdf`;

const PERKS = [
  "AI-generated career report tailored to your grade & board",
  "Top 3 career matches with % fit",
  "Personality, aptitude & interest breakdown",
  "3-stage NEP-aligned roadmap",
];

export default function Pay() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  useEffect(() => {
    if (user?.has_paid) nav("/assessment", { replace: true });
    if (user?.phone) setPhone(user.phone);
  }, [user, nav]);

  useEffect(() => {
    const stored = localStorage.getItem("pf_ref_code");
    if (stored) setReferralCode(stored);
  }, []);

  useEffect(() => {
    const params = referralCode.trim() ? { referral_code: referralCode.trim() } : {};
    api.get("/payment/status", { params }).then((r) => {
      setPrice(r.data.price_breakdown);
      setCouponApplied(r.data.coupon_applied);
    }).catch(() => {});
  }, [referralCode]);

  const payNow = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{10}$/.test(phone)) {
      setErr("Enter a valid 10-digit phone number.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payment/create-order", { phone, referral_code: referralCode.trim() || undefined });
      const Cashfree = await loadCashfreeSdk();
      const cashfree = Cashfree({ mode: data.cashfree_mode || "sandbox" });
      cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="card-brutal p-8">
          <span className="label-mono">One-time payment</span>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-2">Unlock your assessment</h1>
          <p className="text-sm text-[#52525B] mt-2">Pay once, take (and retake) your AI career assessment anytime.</p>

          <div className="mt-6 flex items-baseline gap-2">
            <span className="font-display text-5xl font-extrabold">₹{price ? price.total.toFixed(2) : "…"}</span>
            <span className="label-mono text-[#52525B]">one-time</span>
          </div>

          {price && (
            <div className="mt-4 border-2 border-[#0A0A0A] rounded-xl p-4 bg-white text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#52525B]">Assessment fee</span><span>₹{price.base_price.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#52525B]">Platform fee</span><span>₹{price.platform_fee.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#52525B]">GST ({Math.round(price.gst_rate * 100)}%)</span><span>₹{price.gst_amount.toFixed(2)}</span></div>
              <div className="flex justify-between font-bold pt-1.5 border-t-2 border-[#0A0A0A]"><span>Total</span><span>₹{price.total.toFixed(2)}</span></div>
            </div>
          )}

          <ul className="mt-6 space-y-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm">
                <CheckCircle2 size={18} strokeWidth={2.5} className="text-blue-600 shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>

          <form onSubmit={payNow} className="mt-8 space-y-4">
            <div>
              <label className="label-mono block mb-2">Phone number (for payment receipt)</label>
              <input
                data-testid="pay-phone"
                required
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="label-mono block mb-2">Have a partner code?</label>
              <input
                data-testid="pay-referral-code"
                placeholder="Optional coupon / partner code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              />
              {referralCode.trim() && (
                <div data-testid="pay-coupon-status" className={`mt-1.5 text-xs font-semibold ${couponApplied ? "text-green-700" : "text-[#52525B]"}`}>
                  {couponApplied ? "₹100 discount applied" : "Code not recognized — full price will apply"}
                </div>
              )}
            </div>

            {err && <div data-testid="pay-error" className="text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}

            <div className="flex flex-col sm:flex-row gap-3">
              <button type="submit" data-testid="pay-submit" disabled={loading} className="btn-brutal bg-blue-600 text-white flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} strokeWidth={2.5} />}
                {loading ? "Redirecting to payment…" : `Pay ${price ? `₹${price.total.toFixed(2)}` : "now"}`}
              </button>
              <a
                href={SAMPLE_REPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sample-report-link"
                className="btn-brutal bg-white flex-1 py-3 flex items-center justify-center gap-2"
              >
                <FileText size={16} strokeWidth={2.5} /> Sample Report
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
